import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Target, Users, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo, stats } from '../data/mock';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Safety First',
      description: 'We maintain the highest safety standards on every project.'
    },
    {
      icon: Target,
      title: 'Quality Excellence',
      description: 'Every detail matters. We deliver exceptional quality.'
    },
    {
      icon: Users,
      title: 'Client Partnership',
      description: 'We build lasting relationships through collaboration.'
    },
    {
      icon: Award,
      title: 'Professional Integrity',
      description: 'Our reputation is built on honesty and reliability.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-28 pb-12 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-4">
              Building Excellence
              <span className="block text-red-500">For Over 40 Years</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed">
              {companyInfo.overview}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-4 bg-white rounded-xl shadow-sm border border-neutral-200">
                <div className="text-3xl font-bold text-red-600 mb-1">{stat.value}</div>
                <div className="text-neutral-600 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-red-600 text-sm font-semibold tracking-widest uppercase">
              Our Mission
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-2 mb-6">
              What Drives Us
            </h2>
            <blockquote className="bg-neutral-50 border border-red-500/30 rounded-xl p-8">
              <p className="text-xl md:text-2xl text-neutral-800 font-light leading-relaxed italic">
                "{companyInfo.mission}"
              </p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-red-600 text-sm font-semibold tracking-widest uppercase">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-2">
              What We Stand For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-neutral-200 hover:border-red-500/50 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                  <value.icon className="w-6 h-6 text-red-600 group-hover:text-white" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">{value.title}</h3>
                <p className="text-neutral-500 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-red-600 text-sm font-semibold tracking-widest uppercase">
                Our Experience
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-2 mb-4">
                Expertise Across
                <span className="block text-red-600">Multiple Sectors</span>
              </h2>
              <div className="space-y-4">
                <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-neutral-900 font-semibold">Hospitality</h3>
                    <span className="text-red-600 font-semibold text-sm">105 Projects</span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-2">
                    <div className="bg-red-600 h-2 rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-neutral-900 font-semibold">Commercial</h3>
                    <span className="text-red-600 font-semibold text-sm">100+ Projects</span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-2">
                    <div className="bg-red-600 h-2 rounded-full" style={{ width: '95%' }} />
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-neutral-900 font-semibold">Residential</h3>
                    <span className="text-red-600 font-semibold text-sm">200+ Units</span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-2">
                    <div className="bg-red-600 h-2 rounded-full" style={{ width: '90%' }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1574848296471-28f79a036f79?w=800&q=80"
                alt="Commercial Building"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 bg-red-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Ready to Work With Us?
          </h2>
          <p className="text-red-100 max-w-2xl mx-auto mb-6">
            Experience the Citiwide difference. Let's discuss how we can bring
            your construction vision to life.
          </p>
          <Link to="/contact">
            <Button className="bg-white hover:bg-neutral-100 text-red-600 font-semibold px-6 py-5">
              Get in Touch
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
